(() => {
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const wait = (duration) => new Promise((resolve) => window.setTimeout(resolve, duration));

  const prepareCredits = (target) => {
    if (!target || target.dataset.cs3TypeReady === 'true') return;
    target.dataset.cs3TypeReady = 'true';

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    target.setAttribute('aria-label', target.textContent.trim());
    target.style.minHeight = target.getBoundingClientRect().height + 'px';

    const walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT);
    const sequence = [];

    while (walker.nextNode()) {
      if (walker.currentNode.nodeValue.trim().length > 0) {
        sequence.push({
          node: walker.currentNode,
          text: walker.currentNode.nodeValue
        });
      }
    }

    sequence.forEach((item) => {
      item.node.nodeValue = '';
    });

    const cursor = document.createElement('span');
    cursor.className = 'cs3-scroll__type-cursor';
    cursor.setAttribute('aria-hidden', 'true');

    const firstNode = sequence[0] && sequence[0].node;
    if (firstNode) firstNode.parentNode.insertBefore(cursor, firstNode.nextSibling);

    const type = async () => {
      if (target.dataset.cs3Typing === 'true') return;
      target.dataset.cs3Typing = 'true';
      for (const item of sequence) {
        item.node.parentNode.insertBefore(cursor, item.node.nextSibling);
        for (const character of item.text) {
          item.node.nodeValue += character;
          await wait(10);
        }
      }
    };

    const creditsPage = target.closest('.cs3-credits-page') || target;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.15)) return;
      observer.disconnect();
      type();
    }, {
      threshold: 0.15,
      rootMargin: '0px'
    });

    observer.observe(creditsPage);
  };

  const init = (root) => {
    if (root.dataset.cs3Ready === 'true') return;
    root.dataset.cs3Ready = 'true';

    const stage = root.querySelector('.cs3-scroll__stage');
    const desktopVideo = root.querySelector('[data-cs3-video="desktop"]');
    const mobileVideo = root.querySelector('[data-cs3-video="mobile"]');
    const progressBar = root.querySelector('[data-cs3-progress]');
    const opening = root.querySelector('.cs3-scroll__opening');
    const openingTitle = root.querySelector('[data-cs3-opening-title]');
    const openingCta = root.querySelector('.cs3-scroll__cta');
    const openingCtaTextNode = openingCta && openingCta.querySelector('span:first-child');
    const lookElements = [...root.querySelectorAll('[data-cs3-look]')];
    const sectionScope = root.closest('.section-case-study-3-scroll') || root.parentElement;
    const credits = sectionScope && sectionScope.querySelector('[data-cs3-credits]');
    const mobileQuery = window.matchMedia('(max-width: 749px)');

    let activeVideo = mobileQuery.matches ? mobileVideo : desktopVideo;
    let frameRequested = false;
    let pendingProgress = 0;
    let titleStarted = false;
    let titleSequenceComplete = false;
    const filmStartSeconds = 2.6;

    const titleText = openingTitle ? openingTitle.textContent.trim() : '';
    const ctaText = openingCtaTextNode ? openingCtaTextNode.textContent.trim() : '';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (openingTitle && titleText) {
      openingTitle.setAttribute('aria-label', titleText);
      openingTitle.textContent = '';
    }

    if (openingCta && openingCtaTextNode && ctaText) {
      openingCta.setAttribute('aria-label', ctaText);
      openingCtaTextNode.textContent = '';
    }

    const revealFilm = () => {
      titleSequenceComplete = true;
      root.classList.add('is-film-visible');

      if (reducedMotion) {
        root.classList.add('is-film-text');
      } else {
        window.setTimeout(() => root.classList.add('is-film-text'), 300);
      }

      update();
    };

    const revealScrollPrompt = async () => {
      if (!opening || !openingCtaTextNode || !ctaText) {
        revealFilm();
        return;
      }

      opening.classList.add('is-cta-visible');

      if (reducedMotion) {
        openingCtaTextNode.textContent = ctaText;
        revealFilm();
        return;
      }

      for (const character of ctaText) {
        openingCtaTextNode.textContent += character;
        await wait(42);
      }

      revealFilm();
    };

    const revealOpeningTitle = async () => {
      if (!openingTitle || !titleText || titleStarted) return;
      titleStarted = true;
      if (opening) opening.classList.add('is-title-visible');

      const textNode = document.createTextNode('');
      const cursor = document.createElement('span');
      cursor.className = 'cs3-scroll__title-cursor';
      cursor.setAttribute('aria-hidden', 'true');
      openingTitle.append(textNode, cursor);

      const completeTitleSequence = async () => {
        cursor.remove();
        await revealScrollPrompt();
      };

      if (reducedMotion) {
        textNode.nodeValue = titleText;
        await completeTitleSequence();
        return;
      }

      for (const character of titleText) {
        textNode.nodeValue += character;
        await wait(26);
      }

      await completeTitleSequence();
    };

    const getLookPhases = () => {
      const filmEnd = Math.max(Number(root.dataset.cs3FilmLength || 700) - 100, 1);
      const centers = lookElements.map((element) => Number(element.dataset.cs3LookPosition || 100) - 50);

      return centers.map((center, index) => {
        const previous = centers[index - 1];
        const next = centers[index + 1];
        const start = index === 0 ? Math.max(0, center - (next - center) / 2) : (previous + center) / 2;
        const end = index === centers.length - 1 ? filmEnd : (center + next) / 2;
        return { start, end };
      });
    };

    let lookPhases = getLookPhases();

    const markReady = () => {
      if (activeVideo && activeVideo.readyState >= 2) root.classList.add('is-ready');
    };

    const requestSeek = () => {
      if (frameRequested) return;
      frameRequested = true;
      window.requestAnimationFrame(seek);
    };

    const seek = () => {
      frameRequested = false;
      if (!activeVideo || !Number.isFinite(activeVideo.duration) || activeVideo.duration <= 0) return;
      if (activeVideo.seeking) return;

      const mediaStart = Math.min(filmStartSeconds, Math.max(activeVideo.duration - 0.04, 0));
      const targetTime = mediaStart + (pendingProgress * Math.max(activeVideo.duration - mediaStart - 0.04, 0));

      if (Math.abs(activeVideo.currentTime - targetTime) > 0.045) {
        activeVideo.currentTime = targetTime;
      }
    };

    const updateLooks = (scrollPositionVh) => {
      lookElements.forEach((element, index) => {
        const phase = lookPhases[index];
        const localProgress = (scrollPositionVh - phase.start) / Math.max(phase.end - phase.start, 1);
        const active = localProgress >= 0 && localProgress < 1;

        if (!active) {
          element.classList.remove('is-active');
          element.setAttribute('aria-hidden', 'true');
          return;
        }

        const y = 105 - (125 * clamp(localProgress, 0, 1));
        element.style.setProperty('--cs3-look-y', y.toFixed(3));
        element.classList.add('is-active');
        element.setAttribute('aria-hidden', 'false');
      });
    };

    const update = () => {
      const rootTop = root.getBoundingClientRect().top + window.scrollY;
      const stageTop = stage ? Math.max(stage.getBoundingClientRect().top, 0) : 0;
      root.style.setProperty('--cs3-stage-offset', stageTop.toFixed(2) + 'px');
      const filmLength = Number(root.dataset.cs3FilmLength || 700);
      const totalDistanceVh = Math.max(filmLength - 100, 1);
      const filmDistance = Math.max(totalDistanceVh * window.innerHeight / 100, 1);
      const relativeScroll = window.scrollY - rootTop;
      const scrollPositionVh = relativeScroll / window.innerHeight * 100;

      pendingProgress = titleSequenceComplete
        ? clamp(relativeScroll / filmDistance, 0, 1)
        : 0;
      root.style.setProperty('--cs3-progress', pendingProgress.toFixed(4));
      if (progressBar) progressBar.style.transform = 'scaleY(' + pendingProgress.toFixed(4) + ')';
      updateLooks(scrollPositionVh);

      requestSeek();
    };

    const selectVideo = () => {
      const nextVideo = mobileQuery.matches ? mobileVideo : desktopVideo;
      if (activeVideo && activeVideo !== nextVideo) activeVideo.pause();
      activeVideo = nextVideo;
      if (!activeVideo) return;
      activeVideo.muted = true;
      activeVideo.pause();
      if (activeVideo.readyState === 0) activeVideo.load();
      markReady();
      update();
    };

    [desktopVideo, mobileVideo].forEach((video) => {
      if (!video) return;
      video.muted = true;
      video.pause();
      video.addEventListener('loadedmetadata', update);
      video.addEventListener('seeked', () => {
        if (video === activeVideo) requestSeek();
      });
      video.addEventListener('loadeddata', markReady);
      video.addEventListener('canplay', markReady);
    });

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', () => {
      lookPhases = getLookPhases();
      update();
    });

    if (mobileQuery.addEventListener) mobileQuery.addEventListener('change', selectVideo);
    else mobileQuery.addListener(selectVideo);

    prepareCredits(credits);

    if (openingTitle && titleText) {
      revealOpeningTitle();
    } else if (opening) {
      revealScrollPrompt();
    }

    selectVideo();
    update();
  };

  const boot = () => document.querySelectorAll('[data-cs3-scroll]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();